import React, { useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Trash2, PencilLine } from "lucide-react";
import { THREE_DOTS_DROPDOWN } from "../../data/commonComponents";
import { useSelector } from "react-redux";

const DROPDOWN_WIDTH = 180; // px

// Custom styles copied from SelectComponent
const dropdownMenuStyles = {
  position: "absolute",
  top: "36px",
  right: "0",
  minWidth: "180px",
  width: "180px",
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  zIndex: 10,
  padding: "6px 0",
  border: "1px solid #eaf6ff",
};
const dropdownOptionStyles = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 18px",
  fontSize: "13px",
  color: "#6B7280",
  //   background: "#F2F9FD",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  margin: "4px 8px",
  transition: "background 0.2s",
};
const dropdownOptionHover = {
  background: "#EFFCFB",
};

export default function ThreeDotsDropdown({ onEdit, onDelete, isEdit = true }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 180,
  });
  const lang = useSelector((state) => state.language.lang);
  const { editReview, addReview, removeReview } = THREE_DOTS_DROPDOWN[lang];

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const leftAr = rect.right + window.scrollX;
      const leftEn = rect.right + window.scrollX - DROPDOWN_WIDTH;
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: lang == "ar" ? leftAr : leftEn,
      });
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) {
        return;
      }
      if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        className=""
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        tabIndex={0}
        aria-label="Row actions"
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        ref={btnRef}
      >
        <MoreVertical size={22} color="#798797" />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              ...dropdownMenuStyles,
              position: "absolute",
              top: dropdownPos.top,
              left: dropdownPos.left,
              minWidth: "180px",
              width: "180px",
              zIndex: 9999,
            }}
          >
            <div
              style={
                hovered === 0
                  ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                  : dropdownOptionStyles
              }
              onMouseEnter={() => setHovered(0)}
              onMouseLeave={() => setHovered(null)}
              tabIndex={0}
              onClick={(e) => {
                setOpen(false);
                onEdit && onEdit(e);
              }}
            >
              <PencilLine size={18} /> {isEdit ? editReview : addReview}
            </div>
            {isEdit && (
              <div
                style={
                  hovered === 1
                    ? { ...dropdownOptionStyles, ...dropdownOptionHover }
                    : dropdownOptionStyles
                }
                onMouseEnter={() => setHovered(1)}
                onMouseLeave={() => setHovered(null)}
                tabIndex={0}
                onClick={(e) => {
                  setOpen(false);
                  onDelete && onDelete(e);
                }}
              >
                <Trash2 size={18} /> {removeReview}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
